import type { IconName } from '../../components/ui/Icon.js';

/**
 * Account configuration menu.
 *
 * Structured after the feature taxonomy a hosted-PBX customer expects — call
 * management, contact centre, media, numbers, reporting, billing — rather than
 * after our table layout. Customers look for "after-hours routing", not for the
 * table it happens to live in.
 *
 * `phase` marks a section whose backend does not exist yet. Placeholders are otherwise NOT
 * listed (owner, 26 September 2026: hide them); Porting stays, because clients will submit porting
 * requests from it. `permission` hides what this user may not touch.
 */
export interface ConfigItem {
  key: string;
  label: string;
  /** Set when there is no backend yet; renders a labelled placeholder. */
  phase?: string;
  permission?: string;
  /** The account module it belongs to (0130): hidden while the module is off. */
  module?: 'dialler' | 'auto_qa';
  /** Count shown as a pill, resolved by the page. */
}

export interface ConfigGroup {
  key: string;
  label: string;
  icon: IconName;
  items: ConfigItem[];
}

export const CONFIG_NAV: ConfigGroup[] = [
  // Grouped by what a person is doing (owner, 26 September 2026: "make the overall grouping more
  // logical"): the same groups as before, with each item where it is looked for, and related groups
  // side by side -- numbers and trunks before the routing that uses them, the contact centre beside
  // the dialler. Keys (and so addresses) are unchanged.
  {
    key: 'overview', label: 'Overview', icon: 'dashboard',
    items: [
      { key: 'dashboard', label: 'Dashboard' },
    ],
  },
  {
    key: 'people', label: 'Users & devices', icon: 'users',
    items: [
      { key: 'extensions', label: 'Extensions', permission: 'extension:read' },
      // Handsets are set up on each extension, under Phone setup.
      { key: 'departments', label: 'Departments', permission: 'extension:read' },
      { key: 'portal-users', label: 'Portal users', permission: 'user:read' },
      // Everything about how a handset GETS its configuration lives here, and
      // only here (the provisioning module's portal half).
      { key: 'provisioning', label: 'Provisioning', permission: 'device:read' },
      // A handset model, its keys and its network, shared by many phones (docs/29).
      { key: 'templates', label: 'Phone templates', permission: 'device:read' },
    ],
  },
  {
    // The client's own contacts (docs/32). Nobody above the account holds phonebook:read, so the
    // console never shows it to staff.
    key: 'phonebook', label: 'Phonebook', icon: 'contacts',
    items: [
      { key: 'contacts', label: 'Contacts', permission: 'phonebook:read' },
      { key: 'companies', label: 'Companies', permission: 'phonebook:read' },
    ],
  },
  {
    key: 'numbers', label: 'Numbers', icon: 'hash',
    items: [
      { key: 'numbers', label: 'Assigned numbers', permission: 'number:read' },
      // Caller IDs that take turns, for campaigns (0137).
      { key: 'number-pools', label: 'Number pools', permission: 'number:read' },
      // Kept while not built: clients will submit porting requests here (owner, 26 September 2026).
      { key: 'porting', label: 'Porting', phase: 'Phase 2', permission: 'port:read' },
    ],
  },
  {
    key: 'connectivity', label: 'Connectivity', icon: 'carrier',
    items: [
      // connectivity:read, not trunk:read: customers see this too -- which
      // carrier carries their calls and whether it is up. Staff get more.
      { key: 'trunks', label: 'Trunks', permission: 'connectivity:read' },
    ],
  },
  {
    // Where a call goes, in and out, and when.
    key: 'routing', label: 'Call routing', icon: 'ivr',
    items: [
      { key: 'inbound', label: 'Inbound routes', permission: 'number:read' },
      { key: 'ivrs', label: 'IVR menus', permission: 'flow:read' },
      { key: 'ring-groups', label: 'Hunt lists', permission: 'extension:read' },
      // With the other places a call is sent (owner, 26 September 2026); the people who answer them
      // stay under Contact centre.
      { key: 'queues', label: 'Queues', permission: 'queue:read' },
      { key: 'schedules', label: 'Hours & schedules', permission: 'extension:read' },
      { key: 'holidays', label: 'Public holidays', permission: 'extension:read' },
      { key: 'outbound', label: 'Outbound routing', permission: 'outboundroute:read' },
      { key: 'dial-permissions', label: 'Dialling restrictions', permission: 'extension:read' },
      // Account-wide only. A person's own forwarding, screening and hot desking
      // are on their extension (docs/26 B3-B5).
      { key: 'screening', label: 'Blocked numbers', permission: 'extension:read' },
    ],
  },
  {
    key: 'features', label: 'Voice features', icon: 'phone',
    items: [
      { key: 'voicemail', label: 'Voicemail', permission: 'voicemail:read' },
      { key: 'recording', label: 'Call recording', permission: 'recording:read' },
      { key: 'conference', label: 'Conference rooms', permission: 'queue:read' },
      { key: 'parking', label: 'Parking lots', permission: 'queue:read' },
      { key: 'feature-codes', label: 'Feature codes', permission: 'tenant:read' },
    ],
  },
  {
    key: 'media', label: 'Media', icon: 'media',
    items: [
      { key: 'prompts', label: 'Prompts & greetings', permission: 'extension:read' },
      { key: 'moh', label: 'Music on hold', permission: 'extension:read' },
    ],
  },
  {
    // The people who answer the queues, and the words they work from.
    key: 'contact-centre', label: 'Contact centre', icon: 'queue',
    items: [
      { key: 'agents', label: 'Agents & skills', permission: 'queue:read' },
      // The words the workspace works from: why somebody paused, and what a call was.
      { key: 'agent-codes', label: 'Agent codes', permission: 'queue:read' },
    ],
  },
  {
    // The people a campaign will call (docs/36 D1b). The client's own, like the phonebook: nobody
    // above the account holds dialler:read, so the console never shows it to staff.
    key: 'dialler', label: 'Dialler', icon: 'callOut',
    items: [
      { key: 'campaigns', label: 'Campaigns', permission: 'campaign:read', module: 'dialler' },
      { key: 'lead-lists', label: 'Lead lists', permission: 'dialler:read', module: 'dialler' },
      { key: 'call-scripts', label: 'Call scripts', permission: 'campaign:read', module: 'dialler' },
      { key: 'do-not-call', label: 'Do not call', permission: 'dialler:read', module: 'dialler' },
    ],
  },
  {
    // Every report (owner, 26 September 2026: "all the reporting analytics under reporting").
    key: 'reporting', label: 'Reporting', icon: 'rates',
    items: [
      { key: 'calls', label: 'Call history', permission: 'cdr:read' },
      { key: 'queue-reports', label: 'Queue performance', permission: 'queue:read' },
      // Every campaign side by side, and each one's analytics.
      { key: 'campaign-reports', label: 'Campaigns', permission: 'campaign:read', module: 'dialler' },
      // Calls transcribed and analysed on the account's own keys (docs/37).
      { key: 'auto-qa', label: 'Auto QA', permission: 'qa:read', module: 'auto_qa' },
    ],
  },
  {
    key: 'billing', label: 'Billing', icon: 'billing',
    items: [
      // Spend against the month's cap and each extension's budget (0132).
      { key: 'spend', label: 'Spend limits', permission: 'cdr:read' },
      { key: 'billing', label: 'Balance & ledger', permission: 'billing:read' },
      { key: 'usage', label: 'Usage breakdown', permission: 'billing:read' },
    ],
  },
  {
    key: 'settings', label: 'Settings', icon: 'settings',
    items: [
      { key: 'profile', label: 'Company profile', permission: 'tenant:read' },
      { key: 'api-keys', label: 'API keys', permission: 'apikey:read' },
      { key: 'audit', label: 'Audit log', permission: 'audit:read' },
    ],
  },
];

/** Which group a section belongs to, so the right group opens on load. */
export function groupFor(itemKey: string): string | null {
  for (const g of CONFIG_NAV) {
    if (g.items.some((i) => i.key === itemKey)) return g.key;
  }
  return null;
}
